import axios from 'axios';

const api = axios.create({
    // Use environment variable for API URL
    baseURL: import.meta.env.VITE_API_URL || '',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // If 401, maybe redirect to login? 
        if (error.response?.status === 401) {
            localStorage.removeItem('admin_token');
            window.location.href = '/login';
        }
        console.error('API Error:', error.response || error.message);
        return Promise.reject(error);
    }
);

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const getDashboardStats = async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
};

export const getDrivers = async (status: string = 'all') => {
    const response = await api.get(`/admin/drivers?status=${status}`);
    return response.data;
};

export const updateDriverStatus = async (id: string, is_verified: boolean) => {
    const response = await api.patch(`/admin/drivers/${id}/status`, { is_verified });
    return response.data;
};

export const toggleDriverAvailability = async (id: string, is_available: boolean) => {
    const response = await api.patch(`/admin/drivers/${id}/availability`, { is_available });
    return response.data;
};

export const verifyDriverDocument = async (id: string, documentType: string, status: string, reason?: string) => {
    const response = await api.patch(`/admin/drivers/${id}/documents`, { documentType, status, reason });
    return response.data;
};

export const updateDriverCategories = async (id: string, categories: string[]) => {
    const response = await api.patch(`/admin/drivers/${id}/categories`, { categories });
    return response.data;
};

export const deleteDriver = async (id: string) => {
    const response = await api.delete(`/admin/drivers/${id}`);
    return response.data;
};

// --- Vehicle Management ---
export const getVehicles = async (status: string = 'all') => {
    const response = await api.get(`/admin/vehicles?status=${status}`);
    return response.data;
};

// --- Tempo Traveller / Bus / Urbania — request-to-book, ops visibility ---
export interface VehicleBookingRequestAdmin {
    id: string;
    trip_group_id: string;
    status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled' | 'confirmed';
    fare_quote_paise: number;
    pickup_address: string | null;
    drop_address: string | null;
    requested_hours: number;
    requested_km: number;
    scheduled_at: string | null;
    expires_at: string;
    decided_at: string | null;
    decline_reason: string | null;
    ride_id: string | null;
    created_at: string;
    vehicle: { id: string; make: string; model: string; plate_number: string } | null;
    rider: { id: string; full_name: string | null; phone: string | null } | null;
    owner: { id: string; profiles: { full_name: string | null; phone: string | null } | null } | null;
}

export const getVehicleBookingRequestsAdmin = async (status?: string): Promise<VehicleBookingRequestAdmin[]> => {
    const response = await api.get('/admin/vehicle-booking-requests', { params: status ? { status } : undefined });
    return response.data.requests;
};

export const updateVehicleStatus = async (id: string, is_verified: boolean, rejection_reason?: string) => {
    const response = await api.patch(`/admin/vehicles/${id}/status`, { is_verified, rejection_reason });
    return response.data;
};

export const updateVehicleCategoryAdmin = async (id: string, vehicle_category_id: string) => {
    const response = await api.patch(`/admin/vehicles/${id}/category`, { vehicle_category_id });
    return response.data;
};

export const verifyVehicleDocument = async (id: string, documentType: string, status: string, reason?: string) => {
    const response = await api.patch(`/admin/vehicles/${id}/documents`, { documentType, status, reason });
    return response.data;
};

export const getRides = async (params?: { page?: number; limit?: number; ride_type?: 'instant' | 'scheduled' | 'scan' }) => {
    const response = await api.get('/admin/rides', { params });
    return response.data;
};

export const getRideDetail = async (id: string) => {
    const response = await api.get(`/admin/rides/${id}`);
    return response.data;
};

// Rider-raised disputes on driver-submitted rental charges. Post-trip review only —
// nothing here blocks a ride, so an unattended queue never strands anyone.
export const getRentalDisputes = async (scope: 'open' | 'resolved' = 'open') => {
    const response = await api.get('/admin/rental/disputes', { params: { scope } });
    return response.data;
};

export const resolveRentalDispute = async (id: string, resolution: 'upheld' | 'refunded', notes?: string) => {
    const response = await api.patch(`/admin/rental/disputes/${id}/resolve`, { resolution, notes });
    return response.data;
};

export const getRentalRides = async (params?: { page?: number; limit?: number; status?: string; service_type?: 'rental_city' | 'rental_outstation' }) => {
    const response = await api.get('/admin/rental/rides', { params });
    return response.data;
};

export const getRentalRideEvents = async (id: string) => {
    const response = await api.get(`/admin/rental/rides/${id}/events`);
    return response.data;
};

export const deleteRide = async (id: string) => {
    const response = await api.delete(`/admin/rides/${id}`);
    return response.data;
};

export const cancelRide = async (id: string, reason: string) => {
    const response = await api.post(`/admin/rides/${id}/cancel`, { reason });
    return response.data;
};

export const getUsers = async () => {
    const response = await api.get('/admin/users');
    return response.data;
};

export const deleteUser = async (id: string) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
};

export const updateUserWallet = async (id: string, amount: number, type: 'add' | 'deduct', password: string) => {
    const response = await api.patch(`/admin/users/${id}/wallet`, { amount, type, password });
    return response.data;
};

export const getUserWalletTransactions = async (id: string, limit = 50) => {
    const response = await api.get(`/admin/users/${id}/wallet/transactions?limit=${limit}`);
    return response.data as { transactions: any[] };
};

export const getAdminPayments = async (params: { type?: string; status?: string; user_id?: string; from?: string; to?: string; limit?: number; page?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.status) qs.set('status', params.status);
    if (params.user_id) qs.set('user_id', params.user_id);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.page) qs.set('page', String(params.page));
    const response = await api.get(`/admin/payments?${qs.toString()}`);
    return response.data as { items: any[]; total: number; page: number; limit: number };
};

export const getAdminUserPaymentHistory = async (userId: string, params: { limit?: number; cursor?: string; type?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.type) qs.set('type', params.type);
    const response = await api.get(`/admin/users/${userId}/payment-history?${qs.toString()}`);
    return response.data as { items: any[]; has_more: boolean; next_cursor: string | null };
};

export const getDriverWalletTransactions = async (id: string, limit = 50) => {
    const response = await api.get(`/admin/drivers/${id}/wallet/transactions?limit=${limit}`);
    return response.data as { transactions: any[] };
};

export const updateDriverWallet = async (id: string, amount: number, type: 'add' | 'deduct', password: string) => {
    const response = await api.patch(`/admin/drivers/${id}/wallet`, { amount, type, password });
    return response.data;
};

export const getCommuteBookings = async (params: { status?: string; route_id?: string; search?: string; limit?: number; page?: number; date_from?: string; date_to?: string; booking_type?: string; sort?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.route_id) qs.set('route_id', params.route_id);
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.page) qs.set('page', String(params.page));
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.booking_type) qs.set('booking_type', params.booking_type);
    if (params.sort) qs.set('sort', params.sort);
    const response = await api.get(`/admin/commute/bookings?${qs.toString()}`);
    return response.data as { bookings: any[]; total: number; page: number; limit: number };
};

export const getRidesAnalytics = async (period: '7d' | '30d' | '90d' = '30d') => {
    const response = await api.get(`/admin/analytics/rides?period=${period}`);
    return response.data;
};

export const getCommuteAnalytics = async (period: '7d' | '30d' | '90d' = '30d') => {
    const response = await api.get(`/admin/analytics/commute?period=${period}`);
    return response.data;
};

export const getTransactions = async (status: string = 'all', dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams({ status });
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const response = await api.get(`/admin/transactions?${params.toString()}`);
    return response.data;
};

export const getRefunds = async (dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const qs = params.toString();
    const response = await api.get(`/admin/refunds${qs ? `?${qs}` : ''}`);
    return response.data;
};

export interface ServiceReadiness {
    /** true = priced and ready, false = on/off works but customers would see nothing, null = could not check */
    ready: boolean | null;
    summary: string;
    issues: string[];
}
export interface PlatformService {
    id: string;
    name: string;
    description: string;
    icon: string;
    group: 'rides' | 'rentals' | 'trips';
    is_active: boolean;
    default_enabled: boolean;
    readiness: ServiceReadiness;
}
export const getServices = async (): Promise<PlatformService[]> => {
    const response = await api.get('/admin/services');
    return response.data;
};

export const updateServiceStatus = async (id: string, is_active: boolean) => {
    const response = await api.patch(`/admin/services/${id}`, { is_active });
    return response.data;
};

export const getAdminWallet = async () => {
    const response = await api.get('/admin/wallet');
    return response.data;
};

export const getRevenueByVehicleCategory = async (params?: { dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/admin/revenue/by-vehicle-category', { params });
    return response.data as {
        rows: { vehicle_type: string; display_name: string; revenue: number; ride_count: number; share_pct: number }[];
        total_revenue: number;
    };
};

// App Settings (Pricing, Limits, Commission)
export const getSettings = async () => {
    const response = await api.get('/admin/settings');
    return response.data;
};

export const updateSetting = async (key: string, value: string) => {
    const response = await api.post('/admin/settings', { key, value });
    return response.data;
};

export interface TermsSection {
    title: string;
    body: string;
}

export interface TermsDocument {
    version: string;
    sections: TermsSection[];
    createdAt: string;
    createdByEmail?: string | null;
    affectedCount: number;
}

export const getTermsDocument = async (role: 'customer' | 'partner'): Promise<TermsDocument> => {
    const response = await api.get(`/admin/terms-documents/${role}`);
    return response.data;
};

export const publishTermsDocument = async (
    role: 'customer' | 'partner',
    sections: TermsSection[],
    basedOnVersion: string,
) => {
    const response = await api.post(`/admin/terms-documents/${role}`, { sections, based_on_version: basedOnVersion });
    return response.data;
};

export const getReferralSettings = async () => {
    const all = await getSettings();
    return {
        referral_enabled:                    all.referral_enabled                    ?? 'true',
        referral_expiry_days:                all.referral_expiry_days                ?? '90',
        // Driver-refers-driver — legacy flat model, unchanged
        referral_reward_referrer_driver:     all.referral_reward_referrer_driver     ?? '100',
        referral_reward_referred_driver:     all.referral_reward_referred_driver     ?? '50',
        referral_min_trips_driver:           all.referral_min_trips_driver           ?? '3',
        // User-refers-user — new 3-event model
        referral_reward_code_applied_bonus:  all.referral_reward_code_applied_bonus  ?? '500',
        referral_reward_first_ride:          all.referral_reward_first_ride          ?? '20',
        referral_reward_first_pass:          all.referral_reward_first_pass          ?? '500',
        referral_redemption_pct_ride:        all.referral_redemption_pct_ride        ?? '10',
        referral_redemption_full_for_pass:   all.referral_redemption_full_for_pass   ?? 'true',
        // Legacy user keys — kept for backward compatibility with any old referral rows
        // that still carry a locked-in reward_amount_referrer/referred value
        referral_reward_referrer_user:       all.referral_reward_referrer_user       ?? '50',
        referral_reward_referred_user:       all.referral_reward_referred_user       ?? '50',
        referral_min_amount_user:            all.referral_min_amount_user            ?? '100',
        referral_min_rides_user:             all.referral_min_rides_user             ?? '1',
    };
};

// Support Tickets
export const getTickets = async (filters?: { status?: string; priority?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.priority && filters.priority !== 'all') params.append('priority', filters.priority);
    const response = await api.get(`/admin/tickets?${params.toString()}`);
    return response.data;
};

export const updateTicketStatus = async (id: string, updates: { status?: string; priority?: string }) => {
    const response = await api.patch(`/admin/tickets/${id}`, updates);
    return response.data;
};

export const getTicketById = async (id: string) => {
    const response = await api.get(`/admin/tickets/${id}`);
    return response.data;
};

export const replyToTicket = async (id: string, message: string) => {
    const response = await api.post(`/admin/tickets/${id}/reply`, { message });
    return response.data;
};

// ==================== Service Areas (Geofencing) ====================

export const getServiceAreas = async () => {
    const response = await api.get('/admin/service-areas');
    return response.data;
};

export const createServiceArea = async (data: { name: string; polygon: Array<{ lat: number; lng: number }>; type?: string; is_active?: boolean; price_multiplier?: number | null }) => {
    const response = await api.post('/admin/service-areas', data);
    return response.data;
};

export const updateServiceArea = async (id: string, data: { name?: string; polygon?: Array<{ lat: number; lng: number }>; type?: string; is_active?: boolean; price_multiplier?: number | null }) => {
    const response = await api.patch(`/admin/service-areas/${id}`, data);
    return response.data;
};

export const deleteServiceArea = async (id: string) => {
    const response = await api.delete(`/admin/service-areas/${id}`);
    return response.data;
};

// ==================== Config ====================
export const getGoogleMapsApiKey = async () => {
    const response = await api.get('/config/maps-key');
    return response.data.key;
};

// ==================== Commute System (Route-Based) ====================

// Routes
export const getCommuteRoutes = async () => {
    const response = await api.get('/commute/admin/routes');
    return response.data;
};

export const createCommuteRoute = async (data: any) => {
    const response = await api.post('/commute/admin/routes', data);
    return response.data;
};

export const getCommuteRouteDetail = async (id: string) => {
    const response = await api.get(`/commute/admin/routes/${id}`);
    return response.data;
};

export const updateCommuteRoute = async (id: string, data: any) => {
    const response = await api.patch(`/commute/admin/routes/${id}`, data);
    return response.data;
};

export const syncAllCommuteTrips = async () => {
    const response = await api.post('/commute/admin/trips/sync', undefined, { timeout: 120000 });
    return response.data;
};

// Stops
export const setCommuteStops = async (routeId: string, stops: any[]) => {
    const response = await api.post(`/commute/admin/routes/${routeId}/stops`, { stops });
    return response.data;
};

export const reorderCommuteStops = async (routeId: string, stops: any[]) => {
    const response = await api.put(`/commute/admin/routes/${routeId}/stops/reorder`, { stops });
    return response.data;
};

export const updateCommuteRoutePath = async (routeId: string, polyline: string) => {
    const response = await api.post(`/commute/admin/routes/${routeId}/path`, { polyline });
    return response.data;
};

// Assignments
export const assignCommuteDriver = async (routeId: string, data: { driver_id: string; vehicle_id?: string }) => {
    const response = await api.post(`/commute/admin/routes/${routeId}/assign-driver`, data);
    return response.data;
};

// Multi-Driver Assignment endpoints
export const addCommuteAssignment = async (routeId: string, data: { driver_id: string; vehicle_id?: string; label: string; capacity: number }) => {
    const response = await api.post(`/commute/admin/routes/${routeId}/assignments`, data);
    return response.data;
};

export const listCommuteAssignments = async (routeId: string) => {
    const response = await api.get(`/commute/admin/routes/${routeId}/assignments`);
    return response.data;
};

export const updateCommuteAssignment = async (routeId: string, assignmentId: string, data: any) => {
    const response = await api.patch(`/commute/admin/routes/${routeId}/assignments/${assignmentId}`, data);
    return response.data;
};

export const getAssignmentRemovalImpact = async (routeId: string, assignmentId: string) => {
    const response = await api.get(`/commute/admin/routes/${routeId}/assignments/${assignmentId}/removal-impact`);
    return response.data;
};

export const purgeInactiveAssignment = async (routeId: string, assignmentId: string) => {
    const response = await api.delete(`/commute/admin/routes/${routeId}/assignments/${assignmentId}/purge`);
    return response.data;
};

export interface RemoveAssignmentPayload {
    action?: 'keep_running' | 'cancel_trips' | 'replace';
    // cancel_trips
    reason?: string;
    message?: string;
    // replace
    replacement_driver_id?: string;
    replacement_vehicle_id?: string;
    replacement_label?: string;
    replacement_capacity?: number;
}

export const removeCommuteAssignment = async (
    routeId: string,
    assignmentId: string,
    payload: RemoveAssignmentPayload = {}
) => {
    const response = await api.delete(
        `/commute/admin/routes/${routeId}/assignments/${assignmentId}`,
        { data: payload }
    );
    return response.data;
};



// Waitlist
export const getCommuteWaitlist = async (status: string = 'pending', routeId?: string) => {
    const params = new URLSearchParams();
    params.append('status', status);
    if (routeId) params.append('route_id', routeId);
    const response = await api.get(`/commute/admin/waitlist?${params.toString()}`);
    return response.data;
};

export const updateCommuteWaitlistEntry = async (
    id: string,
    data: {
        status: string;
        route_id?: string;
        stop_id?: string;
        pickup_stop_id?: string;
        drop_stop_id?: string;
        assignment_id?: string;
        booking_type?: string;
    }
) => {
    const response = await api.patch(`/commute/admin/waitlist/${id}`, data);
    return response.data;
};


export const getCommuteTrips = async (filters?: { date?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.date) params.append('date', filters.date);
    if (filters?.status) params.append('status', filters.status);
    const response = await api.get(`/commute/admin/trips?${params.toString()}`);
    return response.data;
};

export const getCommuteTripDetail = async (id: string) => {
    const response = await api.get(`/commute/admin/trips/${id}`);
    return response.data;
};

// Route deletion
export const deleteCommuteRoute = async (id: string) => {
    const response = await api.delete(`/commute/admin/routes/${id}`);
    return response.data;
};

// Get active and upcoming trips for a route
export const getActiveTripsForRoute = async (routeId: string) => {
    const response = await api.get(`/commute/admin/trips?route_id=${routeId}&status=not_started,ongoing,delayed`);
    return response.data;
};


// Reassign a pass to a different route
export const reassignCommutePass = async (passId: string, data: { new_route_id: string; new_pickup_stop_id: string; new_drop_stop_id: string }) => {
    const response = await api.post(`/admin/passes/${passId}/reassign`, data);
    return response.data;
};

// Monitoring & Failure Handling
export const getCommuteSystemHealth = async () => {
    const response = await api.get('/commute/admin/monitoring/health');
    return response.data;
};

export const reassignCommuteTrip = async (id: string, data: { driver_id: string; vehicle_id: string }) => {
    const response = await api.patch(`/commute/admin/trips/${id}/reassign`, data);
    return response.data;
};

// Auto Match: when on, CommuteWatcherService.autoFillDriverlessTrips assigns the nearest
// eligible driver if this trip has none — manual reassignment never touches this flag.
export const updateCommuteTripAutoMatch = async (id: string, autoMatchEnabled: boolean) => {
    const response = await api.patch(`/commute/admin/trips/${id}`, { auto_match_enabled: autoMatchEnabled });
    return response.data;
};

// Driver & Vehicle lists for assignment
export const getCommuteDriversList = async (sameCategoryAsAssignment?: string) => {
    const params = sameCategoryAsAssignment
        ? `?same_category_as_assignment=${sameCategoryAsAssignment}`
        : '';
    const response = await api.get(`/commute/admin/drivers${params}`);
    return response.data;
};

export const getCommuteVehiclesByDriver = async (driverId: string) => {
    const response = await api.get(`/commute/admin/drivers/${driverId}/vehicles`);
    return response.data;
};

// User search for assignment
export const getAdminNotifications = async (limit: number = 25, offset: number = 0) => {
    const response = await api.get(`/admin/notifications?limit=${limit}&offset=${offset}`);
    return response.data;
};

export const markAdminNotificationRead = async (id: string) => {
    const response = await api.put('/admin/notifications/read', { id });
    return response.data;
};

export const markAllAdminNotificationsRead = async () => {
    const response = await api.put('/admin/notifications/read-all');
    return response.data;
};

// New (unread) payment notifications count — drives the Finance card badge.
export const getNewPaymentsCount = async (): Promise<number> => {
    const response = await api.get('/admin/notifications/payments/unread-count');
    return response.data?.count || 0;
};

// Mark payment notifications seen (called when admin opens the Transactions page).
export const markPaymentsSeen = async () => {
    const response = await api.put('/admin/notifications/payments/seen');
    return response.data;
};

// ─── Custom Notifications (admin → riders/drivers broadcast) ───────────────

export interface SendCustomNotificationPayload {
    title: string;
    message: string;
    type: 'general' | 'promo' | 'alert' | 'update';
    targetType: 'all_users' | 'all_drivers' | 'all' | 'specific';
    targetIds?: string[];
}

export const sendCustomNotification = async (payload: SendCustomNotificationPayload) => {
    const response = await api.post('/admin/notifications/custom', payload);
    return response.data;
};

export const getCustomNotificationHistory = async (limit: number = 25, offset: number = 0) => {
    const response = await api.get(`/admin/notifications/custom?limit=${limit}&offset=${offset}`);
    return response.data;
};

export const getCustomNotificationRecipients = async (id: string) => {
    const response = await api.get(`/admin/notifications/custom/${id}/recipients`);
    return response.data;
};

// ─── Driver Notices (admin → single driver, e.g. after a bad rating) ───────

export interface SendDriverNoticePayload {
    driver_id: string;
    title: string;
    message: string;
    reason?: string;
    related_rating?: number;
}

export const sendDriverNotice = async (payload: SendDriverNoticePayload) => {
    const response = await api.post('/admin/driver-notices', payload);
    return response.data;
};

export const getDriverNotices = async (params: { driver_id?: string; status?: 'unacknowledged' | 'acknowledged' | 'all' } = {}) => {
    const qs = new URLSearchParams();
    if (params.driver_id) qs.set('driver_id', params.driver_id);
    if (params.status) qs.set('status', params.status);
    const response = await api.get(`/admin/driver-notices?${qs.toString()}`);
    return response.data;
};

// ─── Activity log (Super Admin): what each admin login changed ─────────────
export interface ActivityLogEntry {
    id: string;
    actor_id: string | null;
    action: 'ADMIN_API_WRITE' | 'ADMIN_LOGIN' | 'ADMIN_LOGIN_FAILED';
    target_resource: string | null;
    details: { email?: string | null; roles?: string[]; params?: Record<string, string>; body?: unknown; status?: number; reason?: string; role?: string } | null;
    created_at: string;
}

export const getActivityLog = async (params: { page?: number; limit?: number; actor_id?: string; action?: string; q?: string; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    const response = await api.get(`/admin/activity-log?${qs.toString()}`);
    return response.data as { entries: ActivityLogEntry[]; total: number; page: number; limit: number; admins: { id: string; email: string; name?: string | null }[] };
};

// ─── Team & logins (Super Admin) ────────────────────────────────────────────

export interface TeamLogin {
    id: string;
    email: string;
    name: string | null;
    role: string;
    roles: string[];
    created_at: string;
    disabled_at: string | null;
}

/** GET /admin/team - every admin-panel login, the person using it, and the roles Super Admin may hand out */
export const getTeam = async () => {
    const response = await api.get('/admin/team');
    return response.data as { logins: TeamLogin[]; roles: string[] };
};

export const createTeamLogin = async (body: { name: string; email: string; role: string; password: string }) => {
    const response = await api.post('/admin/team', body);
    return response.data as { login: TeamLogin };
};

/** Rename any login; disable / re-enable an ops or marketing login (disabling logs the person out). */
export const updateTeamLogin = async (id: string, body: { name?: string; disabled?: boolean }) => {
    const response = await api.patch(`/admin/team/${id}`, body);
    return response.data as { login: TeamLogin };
};

/** New password for an ops or marketing login; the person is logged out at once. */
export const setTeamLoginPassword = async (id: string, password: string) => {
    const response = await api.post(`/admin/team/${id}/password`, { password });
    return response.data as { login: TeamLogin };
};

// ─── Referral System (Admin) ────────────────────────────────────────────────

/** GET /admin/referrals/dashboard - Aggregate referral metrics */
export const getAdminReferralDashboard = async () => {
    const response = await api.get('/admin/referrals/dashboard');
    return response.data;
};

/** GET /admin/referrals - Paginated list of all referrals with filters */
export const getAdminReferrals = async (params: {
    page?: number;
    limit?: number;
    status?: string;
    role?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
}) => {
    const response = await api.get('/admin/referrals', { params });
    return response.data;
};

/** GET /admin/referrals/:id - Single referral detail */
export const getAdminReferralDetail = async (id: string) => {
    const response = await api.get(`/admin/referrals/${id}`);
    return response.data;
};

/** POST /admin/referrals/:id/process-reward - Manually trigger reward processing */
export const processReferralReward = async (id: string) => {
    const response = await api.post(`/admin/referrals/${id}/process-reward`);
    return response.data;
};

/** POST /admin/referrals/:id/cancel - Cancel/invalidate a referral */
export const cancelReferral = async (id: string, reason: string) => {
    const response = await api.post(`/admin/referrals/${id}/cancel`, { reason });
    return response.data;
};

/** GET /admin/referrals/:id/audit-log - Audit log for a specific referral */
export const getReferralAuditLog = async (id: string) => {
    const response = await api.get(`/admin/referrals/${id}/audit-log`);
    return response.data;
};

/** GET /admin/referrals/:id/reward-events - The up-to-3 reward events for a user-role referral */
export const getReferralRewardEvents = async (id: string) => {
    const response = await api.get(`/admin/referrals/${id}/reward-events`);
    return response.data;
};

/** POST /admin/referrals/:id/reward-events/:eventType/retry - Manually retry a stuck/failed event */
export const retryRewardEvent = async (id: string, eventType: string) => {
    const response = await api.post(`/admin/referrals/${id}/reward-events/${eventType}/retry`);
    return response.data;
};

/** GET /admin/referral-redemptions - Paginated, filterable referral-balance redemption history */
export const getAdminReferralRedemptions = async (params?: {
    page?: number; limit?: number; user_id?: string; booking_type?: string;
    status?: string; from?: string; to?: string;
}) => {
    const response = await api.get('/admin/referral-redemptions', { params });
    return response.data;
};

// ── Coupon Management ─────────────────────────────────────────────────────────

export const getAdminCoupons = async (params?: {
    page?: number; limit?: number; search?: string;
    active?: string; applicable_on?: string;
    sort_by?: string; sort_order?: string;
}) => {
    const response = await api.get('/admin/coupons', { params });
    return response.data;
};

export const getAdminCouponStats = async () => {
    const response = await api.get('/admin/coupons/stats');
    return response.data;
};

// ── Maintenance Mode ───────────────────────────────────────────────────────────

export const getMaintenance = async () => {
    const response = await api.get('/admin/maintenance');
    return response.data as { maintenance: boolean; whitelist: string[] };
};

export const toggleMaintenance = async (enabled: boolean) => {
    const response = await api.post('/admin/maintenance', { enabled });
    return response.data as { maintenance: boolean; whitelist: string[] };
};

export const updateMaintenanceWhitelist = async (userId: string, action: 'add' | 'remove') => {
    const response = await api.post('/admin/maintenance/whitelist', { userId, action });
    return response.data as { whitelist: string[] };
};

export const getAdminCoupon = async (id: string) => {
    const response = await api.get(`/admin/coupons/${id}`);
    return response.data;
};

export const createAdminCoupon = async (data: {
    code: string; discount_type: 'flat' | 'percentage'; discount_value: number;
    min_order_value?: number; max_discount?: number | null;
    usage_limit?: number; per_user_limit?: number;
    valid_from: string; valid_till: string;
    applicable_on?: 'ride' | 'commute' | 'rental' | 'both';
    first_time_only?: boolean; active?: boolean;
}) => {
    const response = await api.post('/admin/coupons', data);
    return response.data;
};

export const updateAdminCoupon = async (id: string, data: Partial<Parameters<typeof createAdminCoupon>[0]>) => {
    const response = await api.patch(`/admin/coupons/${id}`, data);
    return response.data;
};

export const toggleAdminCoupon = async (id: string) => {
    const response = await api.patch(`/admin/coupons/${id}/toggle`);
    return response.data;
};

export const deleteAdminCoupon = async (id: string) => {
    const response = await api.delete(`/admin/coupons/${id}`);
    return response.data;
};

// ── Commute Cancellation ──────────────────────────────────────────────────────

export const cancelCommuteTrip = async (tripId: string, data: { message: string; reason?: string; coupon_ids?: string[] }) => {
    const response = await api.post(`/commute/admin/trips/${tripId}/cancel`, data);
    return response.data;
};

export const issueTripCoupons = async (tripId: string, coupon_ids: string[]) => {
    const response = await api.post(`/commute/admin/trips/${tripId}/issue-coupons`, { coupon_ids });
    return response.data;
};

export const getCancellationStats = async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/commute/admin/cancellation-stats', { params });
    return response.data;
};

// ── Monthly Pass Management ───────────────────────────────────────────────────

export const getAdminPasses = async (params?: { route_id?: string; valid_on?: string; status?: string; pass_type?: string; search?: string; page?: number; limit?: number }) => {
    const response = await api.get('/admin/passes', { params });
    return response.data as { success: boolean; passes: any[]; total: number; page: number; limit: number };
};

export const getAdminPassAuditLog = async (passId: string) => {
    const response = await api.get(`/admin/passes/${passId}/audit-log`);
    return response.data as { events: Array<{ id: string; actor_id: string | null; actor_role: string; actor_label: string; action: string; metadata: any; created_at: string }> };
};

export const getAdminPassStats = async (params?: { dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/admin/passes/stats', { params });
    return response.data;
};

export const getRoutePassHolders = async (routeId: string, params?: { month_start?: string }) => {
    const response = await api.get(`/admin/commute/routes/${routeId}/passes`, { params: { status: 'active', ...params } });
    return response.data;
};

export const cancelAdminPass = async (passId: string, data: { refund_amount: number; reason?: string }) => {
    const response = await api.post(`/admin/passes/${passId}/cancel`, data);
    return response.data;
};

export const getPassRouteCapacity = async (route_id: string, pass_type: 'monthly' | 'weekly') => {
    const response = await api.get('/admin/passes/route-capacity', { params: { route_id, pass_type } });
    return response.data as { seat_limit: number | null; active_pass_count: number; available_seats: number | null };
};

export const grantAdminPass = async (data: {
    user_id: string;
    route_id: string;
    pass_type: 'monthly' | 'weekly';
    pickup_stop_id: string;
    drop_stop_id: string;
    start_date: string;
    reason?: string;
}) => {
    const response = await api.post('/admin/passes/grant', data);
    return response.data;
};

export const lookupUsers = async (q: string) => {
    const response = await api.get('/admin/users', { params: { q } });
    return response.data as Array<{ id: string; full_name: string; phone: string }>;
};

export const updateAdminPassEndDate = async (passId: string, data: { new_end_date: string; reason?: string }) => {
    const response = await api.patch(`/admin/passes/${passId}/end-date`, data);
    return response.data;
};

// ── Vehicle Categories ────────────────────────────────────────────────────────

export const getVehicleCategories = async () => {
    const response = await api.get('/admin/vehicle-categories');
    return response.data;
};

export const createVehicleCategory = async (data: {
    key: string; display_name: string; description?: string;
    capacity: number; base_fare?: number; price_per_km?: number;
    scheduled_base_fare?: number | null; scheduled_price_per_km?: number | null;
    active?: boolean; sort_order?: number;
    supports_cab?: boolean; supports_commute?: boolean; supports_scheduled?: boolean;
    supports_rental_city?: boolean;
    image_base64?: string; file_extension?: string;
    base_distance?: number; max_distance?: number | null;
    policy_items?: { title: string; description?: string }[];
    reference_vehicle_config?: any;
}) => {
    const response = await api.post('/admin/vehicle-categories', data);
    return response.data;
};

export const updateVehicleCategory = async (id: string, data: Partial<Parameters<typeof createVehicleCategory>[0]>) => {
    const response = await api.patch(`/admin/vehicle-categories/${id}`, data);
    return response.data;
};

/** PATCH /admin/vehicles/:id/config — admin correction of a specific vehicle's
 * self-declared configuration. Only meaningful for categories that require it. */
export const updateVehicleConfigAdmin = async (vehicleId: string, vehicleConfig: any) => {
    const response = await api.patch(`/admin/vehicles/${vehicleId}/config`, { vehicle_config: vehicleConfig });
    return response.data;
};

export interface VehiclePhoto {
    id: string;
    vehicle_id: string;
    photo_url: string;
    photo_type: 'interior' | 'exterior';
    sort_order: number;
    created_at: string;
}

/** GET /admin/vehicles/:id/photos — this vehicle's self-declared photo gallery. */
export const getVehiclePhotosAdmin = async (vehicleId: string): Promise<VehiclePhoto[]> => {
    const response = await api.get(`/admin/vehicles/${vehicleId}/photos`);
    return response.data;
};

/** DELETE /admin/vehicle-photos/:photoId — only moderation lever, since photos are
 * auto-visible on upload with no approval gate. */
export const deleteVehiclePhotoAdmin = async (photoId: string) => {
    const response = await api.delete(`/admin/vehicle-photos/${photoId}`);
    return response.data;
};

export const toggleVehicleCategoryStatus = async (id: string) => {
    const response = await api.patch(`/admin/vehicle-categories/${id}/status`);
    return response.data;
};

export const reorderVehicleCategories = async (order: { id: string; sort_order: number }[]) => {
    const response = await api.patch('/admin/vehicle-categories/reorder', { order });
    return response.data;
};

// ── Promo Banners ────────────────────────────────────────────────────────────

export const getPromoBanners = async (audience?: 'driver' | 'user') => {
    const response = await api.get('/admin/promo-banners', { params: audience ? { audience } : {} });
    return response.data;
};

export const createPromoBanner = async (data: {
    image_base64: string;
    link_url?: string | null;
    active?: boolean;
    sort_order?: number;
    audience: 'driver' | 'user';
}) => {
    const response = await api.post('/admin/promo-banners', data);
    return response.data;
};

export const updatePromoBanner = async (id: string, data: Partial<Parameters<typeof createPromoBanner>[0]>) => {
    const response = await api.patch(`/admin/promo-banners/${id}`, data);
    return response.data;
};

export const togglePromoBannerStatus = async (id: string) => {
    const response = await api.patch(`/admin/promo-banners/${id}/status`);
    return response.data;
};

export const reorderPromoBanners = async (order: { id: string; sort_order: number }[]) => {
    const response = await api.patch('/admin/promo-banners/reorder', { order });
    return response.data;
};

export const deletePromoBanner = async (id: string) => {
    const response = await api.delete(`/admin/promo-banners/${id}`);
    return response.data;
};

// ── Scheduled Rides (Admin) ──────────────────────────────────────────────────
export const getScheduledRides = async (params?: { status?: string; page?: number; limit?: number }) => {
    const response = await api.get('/admin/scheduled-rides', { params });
    return response.data;
};

export const getAvailableDriversForScheduled = async (params?: { service_type?: string; vehicle_type?: string }) => {
    const response = await api.get('/admin/scheduled-rides/available-drivers', { params });
    return response.data;
};

export const assignDriverToScheduledRide = async (rideId: string, driverId: string) => {
    const response = await api.post(`/admin/scheduled-rides/${rideId}/assign-driver`, { driver_id: driverId });
    return response.data;
};

export const reassignDriverForScheduledRide = async (
    rideId: string,
    payload: { driver_id: string; reason: string; mark_unavailable?: boolean }
) => {
    const response = await api.post(`/admin/scheduled-rides/${rideId}/reassign-driver`, payload);
    return response.data;
};

export const requestPaymentForScheduledRide = async (rideId: string) => {
    const response = await api.post(`/admin/scheduled-rides/${rideId}/request-payment`);
    return response.data;
};

export const cancelScheduledRideAdmin = async (rideId: string, reason?: string) => {
    const response = await api.post(`/admin/scheduled-rides/${rideId}/cancel`, { reason });
    return response.data;
};

// ── Outstation Rental + Intercity Ride ────────────────────────────────────────
// All amounts are integer paise on the wire; pages convert to/from rupees for display.

export const getOutstationSettings = async () => (await api.get('/admin/outstation/settings')).data;
export const updateOutstationSettings = async (data: Record<string, string>) => (await api.patch('/admin/outstation/settings', data)).data;

export const getOutstationFareConfigs = async (service?: 'outstation' | 'intercity') =>
    (await api.get('/admin/outstation/fare-configs', { params: service ? { service } : {} })).data;
export const createOutstationFareConfig = async (data: object) => (await api.post('/admin/outstation/fare-configs', data)).data;
export const updateOutstationFareConfig = async (id: string, data: object) => (await api.patch(`/admin/outstation/fare-configs/${id}`, data)).data;
export const deactivateOutstationFareConfig = async (id: string) => (await api.delete(`/admin/outstation/fare-configs/${id}`)).data;

export const getOutstationRouteTaxes = async () => (await api.get('/admin/outstation/route-taxes')).data;
export const createOutstationRouteTax = async (data: object) => (await api.post('/admin/outstation/route-taxes', data)).data;
export const updateOutstationRouteTax = async (id: string, data: object) => (await api.patch(`/admin/outstation/route-taxes/${id}`, data)).data;

export const getOutstationAmenities = async () => (await api.get('/admin/outstation/amenities')).data;
export const createOutstationAmenity = async (data: object) => (await api.post('/admin/outstation/amenities', data)).data;
export const updateOutstationAmenity = async (id: string, data: object) => (await api.patch(`/admin/outstation/amenities/${id}`, data)).data;
export const setCategoryAmenities = async (categoryId: string, amenity_ids: string[]) =>
    (await api.put(`/admin/outstation/categories/${categoryId}/amenities`, { amenity_ids })).data;
export const getVehicleAmenities = async (vehicleId: string) => (await api.get(`/admin/outstation/vehicles/${vehicleId}/amenities`)).data;
export const setVehicleAmenities = async (vehicleId: string, amenity_ids: string[]) =>
    (await api.put(`/admin/outstation/vehicles/${vehicleId}/amenities`, { amenity_ids })).data;

export const getOutstationCancellationPolicies = async () => (await api.get('/admin/outstation/cancellation-policies')).data;
export const createOutstationCancellationPolicy = async (data: object) => (await api.post('/admin/outstation/cancellation-policies', data)).data;
export const updateOutstationCancellationPolicy = async (id: string, data: object) => (await api.patch(`/admin/outstation/cancellation-policies/${id}`, data)).data;

export const getOutstationBookings = async (params: { bucket?: string; service?: string; sort?: string; page?: number; limit?: number }) =>
    (await api.get('/admin/outstation/bookings', { params })).data as { bookings: any[]; page: number; limit: number; total: number };
export const getOutstationBooking = async (id: string) => (await api.get(`/admin/outstation/bookings/${id}`)).data;
export const getOutstationEligiblePartners = async (id: string) => (await api.get(`/admin/outstation/bookings/${id}/eligible-partners`)).data;
export const assignOutstationPartner = async (id: string, driver_id: string) => (await api.post(`/admin/outstation/bookings/${id}/assign`, { driver_id })).data;
export const reassignOutstationPartner = async (id: string, driver_id: string, reason: string) =>
    (await api.post(`/admin/outstation/bookings/${id}/reassign`, { driver_id, reason })).data;
export const retryOutstationRoute = async (id: string) => (await api.post(`/admin/outstation/bookings/${id}/retry-route`)).data;
export const setOutstationManualRoute = async (id: string, total_km: number, reason: string) =>
    (await api.post(`/admin/outstation/bookings/${id}/manual-route`, { total_km, reason })).data;
export const adjustOutstationCharges = async (id: string, data: { toll_paise?: number; state_tax_paise?: number; mcd_tax_paise?: number; reason: string }) =>
    (await api.post(`/admin/outstation/bookings/${id}/adjust`, data)).data;
export const addOutstationAdminCharge = async (id: string, data: { charge_type: string; amount_rupees: number; reason: string }) =>
    (await api.post(`/admin/outstation/bookings/${id}/charges`, data)).data;
export const cancelOutstationBooking = async (id: string, reason: string, refund: 'full' | 'policy' | 'none') =>
    (await api.post(`/admin/outstation/bookings/${id}/cancel`, { reason, refund })).data;
export const refundOutstationBooking = async (id: string, amount_rupees: number, reason: string) =>
    (await api.post(`/admin/outstation/bookings/${id}/refund`, { amount_rupees, reason })).data;

export const getOutstationCharges = async (status = 'pending') => (await api.get('/admin/outstation/charges', { params: { status } })).data;
export const approveOutstationCharge = async (id: string, data: { approved_amount_rupees?: number; notes?: string }) =>
    (await api.post(`/admin/outstation/charges/${id}/approve`, data)).data;
export const rejectOutstationCharge = async (id: string, notes: string) => (await api.post(`/admin/outstation/charges/${id}/reject`, { notes })).data;

export const getOutstationAudit = async () => (await api.get('/admin/outstation/audit')).data;

// ── Wedding Car Hire (request-first marketplace) ──────────────────────────────
// All amounts are integer paise on the wire; pages convert to/from rupees for display.

export const getWeddingSettings = async () => (await api.get('/admin/wedding/settings')).data;
export const updateWeddingSettings = async (data: Record<string, string>) => (await api.patch('/admin/wedding/settings', data)).data;

export const getWeddingApprovedMakes = async () => (await api.get('/admin/wedding/approved-makes')).data;
export const createWeddingApprovedMake = async (make: string) => (await api.post('/admin/wedding/approved-makes', { make })).data;
export const updateWeddingApprovedMake = async (id: string, data: { active?: boolean; make?: string }) =>
    (await api.patch(`/admin/wedding/approved-makes/${id}`, data)).data;

export const getWeddingAddons = async () => (await api.get('/admin/wedding/addons')).data;
export const createWeddingAddon = async (data: { name: string; price_paise: number; sort_order?: number }) =>
    (await api.post('/admin/wedding/addons', data)).data;
export const updateWeddingAddon = async (id: string, data: object) => (await api.patch(`/admin/wedding/addons/${id}`, data)).data;

export const getWeddingMinKmRules = async () => (await api.get('/admin/wedding/min-km-rules')).data;
export const upsertWeddingMinKmRule = async (data: { days: number; min_km: number; active?: boolean }) =>
    (await api.put('/admin/wedding/min-km-rules', data)).data;

export const getWeddingDriverAllowance = async () => (await api.get('/admin/wedding/driver-allowance')).data;
export const upsertWeddingDriverAllowance = async (data: { id?: string; vehicle_category_id?: string | null; amount_per_day_paise: number; active?: boolean }) =>
    (await api.put('/admin/wedding/driver-allowance', data)).data;

export const getWeddingCancellationPolicies = async () => (await api.get('/admin/wedding/cancellation-policies')).data;
export const upsertWeddingCancellationPolicy = async (data: { min_hours_before_pickup: number; fee_type: 'flat_paise' | 'percentage'; fee_value: number; active?: boolean; sort_order?: number }) =>
    (await api.put('/admin/wedding/cancellation-policies', data)).data;

export const getWeddingPackageConfig = async (vehicleId: string) => (await api.get(`/admin/wedding/vehicles/${vehicleId}/package-config`)).data;
export const setWeddingPackageConfig = async (vehicleId: string, data: object) => (await api.put(`/admin/wedding/vehicles/${vehicleId}/package-config`, data)).data;

export const getWeddingSearchRequests = async (status?: string) =>
    (await api.get('/admin/wedding/search-requests', { params: status ? { status } : {} })).data;
export const getWeddingBookings = async (params?: { status?: string; from?: string; to?: string }) =>
    (await api.get('/admin/wedding/bookings', { params })).data;
export const getWeddingBooking = async (id: string) => (await api.get(`/admin/wedding/bookings/${id}`)).data;
/** Dry run of "Delete booking": what money goes where, what is released/removed. Changes nothing. */
export const getWeddingBookingDeletePreview = async (id: string, keepPayouts = false) =>
    (await api.get(`/admin/wedding/bookings/${id}/delete-preview`, { params: keepPayouts ? { keep_payouts: 'true' } : {} })).data;
export const deleteWeddingBooking = async (id: string, data: { reason: string; refund_razorpay?: boolean; keep_payouts?: boolean }) =>
    (await api.delete(`/admin/wedding/bookings/${id}`, { data })).data;
export const getWeddingAvailability = async (from?: string, to?: string) =>
    (await api.get('/admin/wedding/availability', { params: { from, to } })).data;

export const getWeddingVehiclePricing = async () => (await api.get('/admin/wedding/vehicles')).data;
export const setWeddingVehiclePriceSlabs = async (vehicleId: string, slabs: { min_km: number; max_km: number | null; price_paise: number }[]) =>
    (await api.put(`/admin/wedding/vehicles/${vehicleId}/price-slabs`, { slabs })).data;

// ── Coach (Urbania Travel & Coach rebuild) ────────────────────────────────────

export interface CoachSettingSpec { type: 'int' | 'enum' | 'uuid_list'; value: string; default: string; min?: number; max?: number; values?: string[] }
export interface CoachCategory {
    id: string; key: string; name: string; brand: string; default_seats: number; default_luggage: number;
    image_url: string | null; description: string | null; active: boolean; sort_order: number;
}
export interface CoachAmenity { id: string; key: string; name: string; icon: string | null; sort_order: number; active: boolean; coach_enabled: boolean }
export type CoachListingStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
export interface CoachListing {
    id: string; make: string; model: string; year: number | null; plate_number: string; color: string | null;
    is_verified: boolean; owner_paused: boolean; owner_driver_id: string | null;
    coach_category_id: string | null; seating_capacity: number | null; luggage_capacity: number | null;
    coach_listing_status: CoachListingStatus | 'none'; coach_listing_note: string | null;
    coach_listing_submitted_at: string | null; coach_listing_reviewed_at: string | null;
    coach_base_city: string | null; coach_base_lat: number | null; coach_base_lng: number | null;
    owner: { id: string; full_name: string | null; phone: string | null } | null;
    coach_category: CoachCategory | null;
    photos: { id: string; photo_url: string; photo_type: string | null }[];
    coach_amenities: { id: string; key: string; name: string; icon: string | null }[];
    coach_listing_missing: string[];
}

export const getCoachSettings = async (): Promise<{ settings: Record<string, CoachSettingSpec> }> =>
    (await api.get('/admin/coach/settings')).data;
export const updateCoachSettings = async (data: Record<string, string>) => (await api.patch('/admin/coach/settings', data)).data;

export const getCoachCategories = async (): Promise<{ categories: CoachCategory[] }> => (await api.get('/admin/coach/categories')).data;
export const createCoachCategory = async (data: Omit<CoachCategory, 'id'>) => (await api.post('/admin/coach/categories', data)).data;
export const updateCoachCategory = async (id: string, data: Partial<Omit<CoachCategory, 'id' | 'key'>>) =>
    (await api.patch(`/admin/coach/categories/${id}`, data)).data;

export const getCoachAmenities = async (): Promise<{ amenities: CoachAmenity[] }> => (await api.get('/admin/coach/amenities')).data;
export const createCoachAmenity = async (data: { key: string; name: string; icon?: string; sort_order?: number }) =>
    (await api.post('/admin/coach/amenities', data)).data;
export const updateCoachAmenity = async (id: string, data: Partial<Omit<CoachAmenity, 'id' | 'key'>>) =>
    (await api.patch(`/admin/coach/amenities/${id}`, data)).data;

export const getCoachListings = async (status: CoachListingStatus): Promise<{ listings: CoachListing[] }> =>
    (await api.get('/admin/coach/listings', { params: { status } })).data;
export const getCoachListing = async (vehicleId: string): Promise<{ listing: CoachListing }> =>
    (await api.get(`/admin/coach/listings/${vehicleId}`)).data;
export const reviewCoachListing = async (vehicleId: string, action: 'approve' | 'reject' | 'suspend', note?: string) =>
    (await api.post(`/admin/coach/listings/${vehicleId}/${action}`, note ? { note } : {})).data;

export type CoachDutyType = 'local' | 'airport' | 'outstation_round_trip' | 'outstation_one_way';
// pg BIGINT columns come back as strings — callers wrap money fields in Number().
type Money = number | string | null;
export interface CoachFareConfig {
    id: string; coach_category_id: string; duty_type: CoachDutyType;
    package_km: number | null; package_hours: number | null; package_fare_paise: Money;
    per_km_rate_paise: Money; min_km_per_day: number | null;
    extra_km_rate_paise: Money; extra_hour_rate_paise: Money; da_per_day_paise: Money;
    active: boolean; category: { id: string; name: string; active: boolean };
}
export interface CoachFareConfigPatch {
    package_km?: number | null; package_hours?: number | null; package_fare_paise?: number | null;
    per_km_rate_paise?: number | null; min_km_per_day?: number | null;
    extra_km_rate_paise?: number | null; extra_hour_rate_paise?: number | null; da_per_day_paise?: number; active?: boolean;
}
export interface CoachCancellationTier {
    id: string; min_hours_before_pickup: number | string; refund_percent: number | string; vendor_share_percent: number | string; active: boolean;
}
export interface CoachChargeType {
    key: string; label: string; pass_through: boolean; commissionable: boolean; requires_proof: boolean; requires_distance: boolean;
    auto_approve_max_paise: Money; active: boolean; sort_order: number;
}

export const getCoachFareConfigs = async (): Promise<{ configs: CoachFareConfig[] }> => (await api.get('/admin/coach/fare-configs')).data;
export const updateCoachFareConfig = async (id: string, data: CoachFareConfigPatch) => (await api.patch(`/admin/coach/fare-configs/${id}`, data)).data;

export const getCoachCancellationTiers = async (): Promise<{ tiers: CoachCancellationTier[] }> => (await api.get('/admin/coach/cancellation-tiers')).data;
export const createCoachCancellationTier = async (data: { min_hours_before_pickup: number; refund_percent: number; vendor_share_percent: number; active?: boolean }) =>
    (await api.post('/admin/coach/cancellation-tiers', data)).data;
export const updateCoachCancellationTier = async (id: string, data: Partial<{ min_hours_before_pickup: number; refund_percent: number; vendor_share_percent: number; active: boolean }>) =>
    (await api.patch(`/admin/coach/cancellation-tiers/${id}`, data)).data;
export const deleteCoachCancellationTier = async (id: string) => (await api.delete(`/admin/coach/cancellation-tiers/${id}`)).data;

// Coach bookings / operations (Phase 3+)
export interface CoachAdminBooking {
    id: string; booking_code: string; status: string; booking_type: 'instant' | 'scheduled'; duty_type: string;
    pickup_address: string; drop_address: string | null; pickup_at: string; return_at: string | null; passengers: number;
    quote_total_paise: number | string; final_total_paise: number | string | null; paid_total_paise: number | string; refunded_total_paise: number | string;
    settlement_status: string; vendor_id: string; driver_id: string | null; user_id: string; created_at: string;
    customer: { id: string; full_name: string | null; phone: string | null } | null;
    vendor: { id: string; full_name: string | null; phone: string | null } | null;
    driver: { id: string; full_name: string | null; phone: string | null } | null;
    vehicle: { id: string; make: string; model: string; plate_number: string } | null;
    pending_charges: number;
    location?: { lat: number; lng: number; updated_at: string } | null;
    pickup_lat?: number; pickup_lng?: number;
}
export interface CoachAdminCharge {
    id: string; booking_id: string; charge_type_key: string; label: string; billing: 'amount' | 'route';
    amount_paise: number | string; approved_amount_paise: number | string | null; distance_km: number | string | null;
    reason: string; proof_url: string | null; stop_address: string | null; stop_lat: number | null; stop_lng: number | null;
    status: string; added_at: string; review_notes: string | null;
    booking: { id: string; booking_code: string; status: string; quote_total_paise: number | string } | null;
}
export interface CoachAdminPenalty {
    id: string; booking_id: string | null; booking_code: string | null; vendor_id: string; trigger: string; label: string;
    proposed_amount_paise: number | string; confirmed_amount_paise: number | string | null; status: string;
    recovery_status: string; recovered_paise: number | string; basis: Record<string, unknown>; decision_notes: string | null; created_at: string;
    vendor: { id: string; full_name: string | null; phone: string | null } | null;
}

export const getCoachStats = async () => (await api.get('/admin/coach/stats')).data as { buckets: Record<string, number>; pending_charges: number; proposed_penalties: number };
export const getCoachBookings = async (bucket: string, q?: string) =>
    (await api.get('/admin/coach/bookings', { params: { bucket, ...(q ? { q } : {}) } })).data as { bookings: CoachAdminBooking[] };
export const getCoachBooking = async (id: string) => (await api.get(`/admin/coach/bookings/${id}`)).data.booking;
export const cancelCoachBookingAdmin = async (id: string, body: { fault: 'vendor' | 'customer' | 'none'; reason: string; trigger?: string; refund_paise?: number }) =>
    (await api.post(`/admin/coach/bookings/${id}/cancel`, body)).data;
export const reassignCoachDriverAdmin = async (id: string, driverId: string, reason?: string) =>
    (await api.post(`/admin/coach/bookings/${id}/reassign-driver`, { driver_id: driverId, ...(reason ? { reason } : {}) })).data;
export const refundCoachBookingAdmin = async (id: string, amountPaise: number, reason: string) =>
    (await api.post(`/admin/coach/bookings/${id}/refund`, { amount_paise: amountPaise, reason })).data;
export const retryCoachSettlement = async (id: string) => (await api.post(`/admin/coach/bookings/${id}/settle-retry`)).data;
export const markCoachNoShowAdmin = async (id: string) => (await api.post(`/admin/coach/bookings/${id}/no-show`)).data;
export const getCoachVendorDrivers = async (vendorId: string) => (await api.get(`/admin/coach/vendors/${vendorId}/drivers`)).data.drivers as {
    id: string; is_verified: boolean; coach_available: boolean; experience_years: number | null; is_self?: boolean; profile: { full_name: string | null; phone: string | null } | null;
}[];
export const getCoachCharges = async (status = 'pending') => (await api.get('/admin/coach/charges', { params: { status } })).data as { charges: CoachAdminCharge[] };
export const reviewCoachCharge = async (id: string, body: { action: 'approve' | 'reject'; approved_amount_paise?: number; notes?: string }) =>
    (await api.post(`/admin/coach/charges/${id}/review`, body)).data;
export const getCoachPenalties = async (status?: string) =>
    (await api.get('/admin/coach/penalties', { params: status ? { status } : {} })).data as { penalties: CoachAdminPenalty[] };
export const createCoachPenalty = async (body: { vendor_id: string; booking_id?: string; amount_paise: number; reason: string }) =>
    (await api.post('/admin/coach/penalties', body)).data;
export const confirmCoachPenalty = async (id: string, body: { amount_paise?: number; notes?: string }) => (await api.post(`/admin/coach/penalties/${id}/confirm`, body)).data;
export const waiveCoachPenalty = async (id: string, notes?: string) => (await api.post(`/admin/coach/penalties/${id}/waive`, notes ? { notes } : {})).data;
export const getCoachMoney = async () => (await api.get('/admin/coach/money')).data;
export const getCoachLive = async () => (await api.get('/admin/coach/live')).data as { trips: CoachAdminBooking[] };

export const getCoachChargeTypes = async (): Promise<{ charge_types: CoachChargeType[] }> => (await api.get('/admin/coach/charge-types')).data;
export const updateCoachChargeType = async (key: string, data: Partial<Omit<CoachChargeType, 'key'>> & { auto_approve_max_paise?: number | null }) =>
    (await api.patch(`/admin/coach/charge-types/${key}`, data)).data;

// ── Rental City Packages ──────────────────────────────────────────────────────

// vehicleCategoryId omitted = every package of every category (used to flag categories with no package).
export const getRentalPackages = async (vehicleCategoryId?: string) => {
    const response = await api.get('/admin/rental/packages', { params: vehicleCategoryId ? { vehicle_category_id: vehicleCategoryId } : undefined });
    return response.data;
};

export const updateRentalPackage = async (id: string, data: object) => {
    const response = await api.patch(`/admin/rental/packages/${id}`, data);
    return response.data;
};

export const createRentalPackage = async (data: object) => {
    const response = await api.post('/admin/rental/packages', data);
    return response.data;
};

// Soft-delete — backend sets active:false, doesn't remove the row (audit trail).
export const deleteRentalPackage = async (id: string) => {
    const response = await api.delete(`/admin/rental/packages/${id}`);
    return response.data;
};

/* ─── Rental dynamic charges (night / peak / festival / waiting / taxes / allowances) ─── */

// vehicleCategoryId omitted = every row, including the all-categories (NULL) ones.
export const getRentalDynamicCharges = async (vehicleCategoryId?: string) => {
    const response = await api.get('/admin/rental/dynamic-charges', {
        params: vehicleCategoryId ? { vehicle_category_id: vehicleCategoryId } : undefined,
    });
    return response.data;
};

export const createRentalDynamicCharge = async (data: object) => {
    const response = await api.post('/admin/rental/dynamic-charges', data);
    return response.data;
};

export const updateRentalDynamicCharge = async (id: string, data: object) => {
    const response = await api.patch(`/admin/rental/dynamic-charges/${id}`, data);
    return response.data;
};

// Soft-delete — backend sets active:false. Reactivate via updateRentalDynamicCharge(id, { active: true }).
export const deleteRentalDynamicCharge = async (id: string) => {
    const response = await api.delete(`/admin/rental/dynamic-charges/${id}`);
    return response.data;
};

/* ─── Commute Driver Payroll ────────────────────────────────────────── */

export const getCommutePayrollCategories = async () => {
    const response = await api.get('/commute/payroll/admin/vehicle-categories');
    return response.data;
};

export const getCommuteSalaryConfigs = async (category?: string) => {
    const response = await api.get('/commute/payroll/admin/salary-config', { params: { category } });
    return response.data;
};

export const createCommuteSalaryConfig = async (data: {
    vehicle_category: string;
    day_rate: number;
    weekly_payout_pct?: number;
    working_days_basis?: string;
    distance_slab_min_km?: number;
    distance_slab_max_km?: number | null;
    effective_from?: string;
    effective_to?: string | null;
}) => {
    const response = await api.post('/commute/payroll/admin/salary-config', data);
    return response.data;
};

export const updateCommuteSalaryConfig = async (id: string, data: object) => {
    const response = await api.patch(`/commute/payroll/admin/salary-config/${id}`, data);
    return response.data;
};

export const getCommuteIncentiveRules = async () => {
    const response = await api.get('/commute/payroll/admin/incentive-rules');
    return response.data;
};

export const createCommuteIncentiveRule = async (data: {
    name: string;
    description?: string;
    min_attendance_pct: number;
    period: string;
    reward_amount: number;
}) => {
    const response = await api.post('/commute/payroll/admin/incentive-rules', data);
    return response.data;
};

export const updateCommuteIncentiveRule = async (id: string, data: object) => {
    const response = await api.patch(`/commute/payroll/admin/incentive-rules/${id}`, data);
    return response.data;
};

export const getCommuteAttendance = async (params?: { date?: string; driver_id?: string; month?: string }) => {
    const response = await api.get('/commute/payroll/admin/attendance', { params });
    return response.data;
};

export const overrideCommuteAttendance = async (id: string, data: { status: string; reason: string }) => {
    const response = await api.patch(`/commute/payroll/admin/attendance/${id}`, data);
    return response.data;
};

export const createCommuteAttendanceOverride = async (data: {
    driver_id: string; route_id: string; date: string; status: string; reason: string;
}) => {
    const response = await api.post('/commute/payroll/admin/attendance', data);
    return response.data;
};

export const getCommutePenalties = async (params?: { status?: string; driver_id?: string }) => {
    const response = await api.get('/commute/payroll/admin/penalties', { params });
    return response.data;
};

export const createCommutePenalty = async (data: {
    driver_id: string; trip_id?: string; type: string; amount: number; reason: string;
}) => {
    const response = await api.post('/commute/payroll/admin/penalties', data);
    return response.data;
};

export const decideCommutePenalty = async (id: string, action: 'approve' | 'reject' | 'cancel') => {
    const response = await api.patch(`/commute/payroll/admin/penalties/${id}`, { action });
    return response.data;
};

export const getCommutePayoutBatches = async (params?: { type?: string; status?: string }) => {
    const response = await api.get('/commute/payroll/admin/batches', { params });
    return response.data;
};

export const getCommutePayoutBatch = async (id: string) => {
    const response = await api.get(`/commute/payroll/admin/batches/${id}`);
    return response.data;
};

export const approveCommutePayoutBatch = async (id: string) => {
    const response = await api.post(`/commute/payroll/admin/batches/${id}/approve`);
    return response.data;
};

export const processCommutePayoutBatch = async (id: string) => {
    const response = await api.post(`/commute/payroll/admin/batches/${id}/process`);
    return response.data;
};

export const generateCommutePayoutBatch = async (type: 'weekly', payDate?: string) => {
    const response = await api.post('/commute/payroll/admin/batches/generate', undefined, {
        params: { type, pay_date: payDate },
    });
    return response.data;
};

export const getCommuteLedger = async (params?: {
    driver_id?: string; entry_type?: string; cycle?: string;
    status?: string; from?: string; to?: string; limit?: number;
}) => {
    const response = await api.get('/commute/payroll/admin/ledger', { params });
    return response.data;
};

export const createCommuteAdjustment = async (data: {
    driver_id: string; amount: number; description: string; settlement_cycle?: string;
}) => {
    const response = await api.post('/commute/payroll/admin/ledger/adjustment', data);
    return response.data;
};

export const getCommuteSalaryPeriods = async (month?: string) => {
    const response = await api.get('/commute/payroll/admin/periods', { params: { month } });
    return response.data;
};

// ── Commute Analytics & Demand ──────────────────────────────────────────

export const getCommuteSearchAnalytics = async (params?: { start_date?: string; end_date?: string; type?: 'pickup' | 'drop' | 'both'; location?: string }) => {
    const response = await api.get('/commute/admin/search-analytics', { params });
    return response.data;
};

export const getCommuteSearchAnalyticsDetail = async (params?: { page?: number; limit?: number; start_date?: string; end_date?: string; has_results?: 'true' | 'false' | ''; location?: string }) => {
    const response = await api.get('/commute/admin/search-analytics/detail', { params });
    return response.data;
};

export const getCommuteRouteRequests = async (params?: { page?: number; limit?: number; status?: string; start_date?: string; end_date?: string; location?: string }) => {
    const response = await api.get('/commute/admin/route-requests', { params });
    return response.data;
};

export const updateCommuteRouteRequest = async (id: string, data: { status?: string; admin_notes?: string }) => {
    const response = await api.patch(`/commute/admin/route-requests/${id}`, data);
    return response.data;
};

export const getRideOperationsOverview = async () => {
    const response = await api.get('/admin/ride-operations/overview');
    return response.data;
};

// ── Quick Chat support ──────────────────────────────────────────────────────
export interface AdminQuickChatMessage {
    id: string;
    scope: 'ride' | 'commute' | 'support' | 'outstation';
    scope_id: string;
    sender_role: 'customer' | 'driver' | 'admin';
    sender_id: string;
    recipient_id: string | null;
    message_id: string;
    body?: string | null;
    created_at: string;
    delivered_at: string | null;
    read_at: string | null;
    status: 'sent' | 'delivered' | 'read';
    customer_name?: string | null;
    customer_phone?: string | null;
}

export const getSupportQuickChat = async (): Promise<AdminQuickChatMessage[]> => {
    const response = await api.get('/quick-chat/admin/support?limit=300');
    return response.data;
};

// ── Quick Chat: ride & commute conversations (admin monitoring) ──────────────
export interface AdminQuickChatConversation {
    scope: 'ride' | 'commute' | 'outstation';
    scope_id: string;
    status: string | null;
    customer_id: string | null;
    customer_name: string | null;
    customer_phone: string | null;
    driver_id: string | null;
    driver_name: string | null;
    driver_phone: string | null;
    last_message_id: string;
    last_message_body: string | null;
    last_sender_role: 'customer' | 'driver' | 'admin';
    last_at: string;
    message_count: number;
    needs_attention: boolean;
}

export const getAdminQuickChatConversations = async (
    scope: 'ride' | 'commute' | 'outstation',
    limit = 100,
): Promise<AdminQuickChatConversation[]> => {
    const response = await api.get(`/quick-chat/admin/conversations/${scope}`, { params: { limit } });
    return response.data;
};

export const getQuickChatThread = async (
    scope: 'ride' | 'commute' | 'outstation',
    scopeId: string,
): Promise<AdminQuickChatMessage[]> => {
    const response = await api.get(`/quick-chat/${scope}/${scopeId}/messages`);
    return response.data;
};

export const sendAdminQuickChatMessage = async (
    scope: 'ride' | 'commute' | 'support' | 'outstation',
    scopeId: string,
    payload: { message_id: string; body?: string; recipient_id?: string },
): Promise<AdminQuickChatMessage> => {
    const response = await api.post(`/quick-chat/${scope}/${scopeId}/message`, payload);
    return response.data;
};

/* ─── Settings registry (labels, help text, units, bounds + current values) ─── */

export interface SettingFieldDef {
    key: string;
    group: string;
    label: string;
    description?: string;
    type: 'number' | 'int' | 'bool' | 'text';
    prefix?: string;
    suffix?: string;
    min?: number;
    max?: number;
    /** A fixed list of choices: shown as a dropdown, and the only values the server accepts. */
    options?: { value: string; label: string }[];
    /** What the backend uses while the setting has never been saved (null = required, no fallback). */
    default: string | null;
    /** Current value in the units shown to the admin (₹, not paise). '' = never saved. */
    value: string;
    is_set: boolean;
}
export interface SettingGroupDef { id: string; title: string; subtitle?: string }
export interface SettingsSchema { groups: SettingGroupDef[]; fields: SettingFieldDef[] }

export const getSettingsSchema = async (): Promise<SettingsSchema> => (await api.get('/admin/settings/schema')).data;

/* ─── Rental rules: add-on blocks, add-on limits, cancellation slabs, pricing tiers ─── */

export type RentalRuleResource = 'addon-blocks' | 'addon-limits' | 'cancellation-policies' | 'pricing-tiers';

export const listRentalRules = async (resource: RentalRuleResource, vehicleCategoryId?: string) =>
    (await api.get(`/admin/rental/${resource}`, { params: vehicleCategoryId ? { vehicle_category_id: vehicleCategoryId } : undefined })).data;
export const createRentalRule = async (resource: RentalRuleResource, data: object) =>
    (await api.post(`/admin/rental/${resource}`, data)).data;
export const updateRentalRule = async (resource: RentalRuleResource, id: string, data: object) =>
    (await api.patch(`/admin/rental/${resource}/${id}`, data)).data;

export default api;

// ── Website leads (www.yann.in forms + campaign attribution) ────────────────
export interface WebsiteLeadFilters {
    from?: string;
    to?: string;
    lead_type?: string;
    status?: string;
    channel?: string;
    campaign?: string;
    q?: string;
    page?: number;
    limit?: number;
    export?: '1';
}

export const getWebsiteLeads = async (filters: WebsiteLeadFilters) => {
    const response = await api.get('/admin/website-leads', { params: filters });
    return response.data;
};

export const getWebsiteLeadSummary = async (range: { from?: string; to?: string }) => {
    const response = await api.get('/admin/website-leads/summary', { params: range });
    return response.data;
};

export const updateWebsiteLead = async (id: string, changes: { status?: string; admin_notes?: string | null }) => {
    const response = await api.patch(`/admin/website-leads/${id}`, changes);
    return response.data;
};

// ── Marketing Dashboard (super_admin + marketing) ───────────────────────────
export interface MarketingFilters {
    from?: string;
    to?: string;
    granularity?: 'day' | 'week';
    audience?: 'all' | 'provider' | 'customer';
    channel?: string;
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    landing_page?: string;
    device?: string;
    lead_status?: string;
}

export const getMarketingOverview = async (filters: MarketingFilters) => {
    const response = await api.get('/admin/marketing/overview', { params: filters });
    return response.data;
};

export interface AdSpendInput {
    spend_date: string;
    platform: 'meta' | 'google' | 'other';
    campaign: string;
    audience: 'provider' | 'customer' | null;
    amount: number;
    impressions: number | null;
    clicks: number | null;
}

export const getAdSpend = async (range: { from?: string; to?: string }) => {
    const response = await api.get('/admin/marketing/ad-spend', { params: range });
    return response.data;
};

export const saveAdSpend = async (rows: AdSpendInput[], entrySource: 'manual' | 'csv') => {
    const response = await api.post('/admin/marketing/ad-spend', { rows, entry_source: entrySource });
    return response.data as { inserted: number; updated: number };
};

export const deleteAdSpend = async (id: string) => {
    const response = await api.delete(`/admin/marketing/ad-spend/${id}`);
    return response.data;
};

/** Records a CSV export in the audit log (the CSV itself is built in the browser). Never blocks the download. */
export const logMarketingExport = (report: string, rows: number, filters?: Record<string, unknown>) =>
    api.post('/admin/marketing/export-log', { report, rows, filters }).catch(() => undefined);
